import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { 
  getHorseList, 
  getHorseDetail, 
  createHorse, 
  updateHorse, 
  deleteHorse,
  getActiveHorses 
} from '@/api/horse'
import { imageCacheUtil } from '@/utils/image-cache'

export const useHorseStore = defineStore('horse', () => {
  // State
  const horses = ref([])
  const activeHorses = ref([])
  const currentHorse = ref(null)
  const loading = ref(false)
  const total = ref(0)
  
  // Filters
  const filters = ref({
    status: '',
    available: null
  })

  // Getters
  const availableHorses = computed(() => 
    horses.value.filter(h => h.status === 'available' || h.available === true)
  )
  
  const busyHorses = computed(() => 
    horses.value.filter(h => h.status === 'busy' || h.available === false)
  )
  
  const horsesCount = computed(() => horses.value.length)
  
  const activeHorsesCount = computed(() => activeHorses.value.length)

  // Actions
  const fetchHorses = async (filterOptions = {}, forceRefresh = false) => {
    loading.value = true
    try {
      const params = {
        page: filterOptions.page || 1,
        pageSize: filterOptions.pageSize || 10,
        ...filterOptions
      }
      
      const res = await getHorseList(params)
      
      if (res.success) {
        // Handle both array and object with list property
        if (Array.isArray(res.data)) {
          horses.value = res.data
          total.value = res.data.length
        } else {
          horses.value = res.data?.list || []
          total.value = res.data?.total || 0
        }
        
        // Warm cache with all horse avatars (force overwrite if refreshing)
        const avatarUrls = horses.value.map(h => h.avatar).filter(Boolean)
        imageCacheUtil.warmCache(avatarUrls, forceRefresh)
        
        if (Object.keys(filterOptions).length > 0) {
          filters.value = { ...filters.value, ...filterOptions }
        }
        
        return { success: true, data: horses.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Horse Store] Fetch error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const fetchActiveHorses = async () => {
    loading.value = true
    try {
      const res = await getActiveHorses()
      
      if (res.success) {
        activeHorses.value = res.data || []
        
        // Warm cache with all active horse avatars
        const avatarUrls = activeHorses.value.map(h => h.avatar).filter(Boolean)
        imageCacheUtil.warmCache(avatarUrls)
        
        return { success: true, data: activeHorses.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Horse Store] Fetch active error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const fetchHorseById = async (id) => {
    loading.value = true
    try {
      const res = await getHorseDetail(id)
      
      if (res.success) {
        currentHorse.value = res.data
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Horse Store] Fetch detail error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const addHorse = async (horseData) => {
    try {
      const res = await createHorse(horseData)
      
      if (res.success) {
        if (res.data) {
          horses.value.unshift(res.data)
          total.value++
          
          // Add to active list if available
          if (res.data.available || res.data.status === 'available') {
            activeHorses.value.unshift(res.data)
          }
        }
        
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Horse Store] Create error:', error)
      return { success: false, error: error.message }
    }
  }

  const modifyHorse = async (id, horseData) => {
    try {
      const res = await updateHorse(id, horseData)
      
      if (res.success) {
        // Update in main list
        const index = horses.value.findIndex(h => h.id === id)
        if (index !== -1) {
          horses.value[index] = { ...horses.value[index], ...horseData }
        }
        
        // Update in active list
        const activeIndex = activeHorses.value.findIndex(h => h.id === id)
        if (activeIndex !== -1) {
          activeHorses.value[activeIndex] = { ...activeHorses.value[activeIndex], ...horseData }
        } else if (horseData.available || horseData.status === 'available') {
          // Add to active list if now available
          if (horses.value.find(h => h.id === id)) {
            activeHorses.value.unshift(horses.value.find(h => h.id === id))
          }
        } else {
          // Remove from active list if not available
          activeHorses.value = activeHorses.value.filter(h => h.id !== id)
        }
        
        // Update current if matches
        if (currentHorse.value?.id === id) {
          currentHorse.value = { ...currentHorse.value, ...horseData }
        }
        
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Horse Store] Update error:', error)
      return { success: false, error: error.message }
    }
  }

  const removeHorse = async (id) => {
    try {
      const res = await deleteHorse(id)
      
      if (res.success) {
        const index = horses.value.findIndex(h => h.id === id)
        if (index !== -1) {
          horses.value.splice(index, 1)
          total.value--
        }
        
        // Remove from active list
        activeHorses.value = activeHorses.value.filter(h => h.id !== id)
        
        // Clear current if matches
        if (currentHorse.value?.id === id) {
          currentHorse.value = null
        }
        
        return { success: true }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Horse Store] Delete error:', error)
      return { success: false, error: error.message }
    }
  }

  const clearCurrentHorse = () => {
    currentHorse.value = null
  }

  const resetFilters = () => {
    filters.value = {
      status: '',
      available: null
    }
  }

  return {
    // State
    horses,
    activeHorses,
    currentHorse,
    loading,
    total,
    filters,
    
    // Getters
    availableHorses,
    busyHorses,
    horsesCount,
    activeHorsesCount,
    
    // Actions
    fetchHorses,
    fetchActiveHorses,
    fetchHorseById,
    addHorse,
    modifyHorse,
    removeHorse,
    clearCurrentHorse,
    resetFilters
  }
})
